import { WButton } from "@/components/button/WButton.tsx";
import { requiredMessage } from "@/components/forms/requiredMessage.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { showModal } from "@/components/modal/registerModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { description_sshKey } from "@/modules/compute/labels.tsx";
import { ParamsTextField } from "@/modules/compute/ParamsTextField.tsx";
import { addSshKeyMutation } from "@/modules/compute/sshKeysQuery.ts";
import { parametersByNameAtom } from "@/modules/compute/vmParametersQuery.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useLingui } from "@lingui/react/macro";
import type { SshKeyCreateBody } from "@warrenio/api-spec/spec.oats.gen";
import { useForm, type SubmitHandler } from "react-hook-form";

interface SshKeysInputsInputs extends SshKeyCreateBody {}

export function AddSshKeyModalContent({ onKeyAdded }: { onKeyAdded?: (value: string) => void }) {
    //#region Hooks
    const { t } = useLingui();
    const params = useSuspenseQueryAtom(parametersByNameAtom);
    const mutation = useStandardMutation(addSshKeyMutation);

    //#endregion

    const nameParams = params.get("name")!;
    const publicKeyParams = params.get("public_key")!;

    const onSubmit: SubmitHandler<SshKeysInputsInputs> = async (inputs) => {
        const { name, public_key } = inputs;

        await mutation.mutateAsync({
            body: {
                name,
                public_key,
            },
        });

        onKeyAdded?.(public_key);
    };

    const form = useForm<SshKeysInputsInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            name: "",
            public_key: "",
        },
    });

    return (
        <WModalForm form={form} onSubmit={onSubmit} title={t`New SSH key`} label={t`Add`}>
            <ParamsTextField headless form={form} field="name" autoFocus params={nameParams} label={t`Name`} />

            <ParamsTextField
                form={form}
                isRequired
                field="public_key"
                params={publicKeyParams}
                label={t`Public key`}
                multilineRows={5}
                headless
                description={description_sshKey}
                registerOptions={{
                    validate: (value: string) => {
                        if (!value) return t(requiredMessage);
                        if (publicKeyParams.constraint === "regexp" && publicKeyParams.expression) {
                            const regexp = new RegExp(publicKeyParams.expression);
                            if (value && !regexp.test(value)) return t`SSH key does not match required pattern`;
                        }
                        return true;
                    },
                }}
            />
        </WModalForm>
    );
}

export function AddSshKeyModal() {
    const { t } = useLingui();
    return (
        <WButton
            variant="border"
            action={() => showModal(<AddSshKeyModalContent />)}
            color="primary"
            label={t`Add SSH key`}
            icon="jp-icon-add"
        />
    );
}
